import { Users, Heart, UserCheck, MessageCircle, Headphones, Globe, PlayCircle, TrendingUp, FileText, MapPin } from 'lucide-react';
import type { Product } from './types';

export const customerSuccessCRMProducts: Product[] = [
  {
    id: 'ai-customer-insights',
    title: 'AI Customer Insights Platform',
    subtitle: 'Intelligent Customer Analytics',
    description: 'Advanced AI platform that analyses customer interactions across all channels to identify trends, sentiment, and growth opportunities for UK market insights.',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&q=80',
    category: 'Customer Success & CRM',
    status: 'Available',
    businessTypes: ['#customer-analytics', '#ai', '#sentiment-analysis', '#customer-insights', '#crm'],
    features: [
      'Multi-channel interaction analysis',
      'Sentiment and emotion detection',
      'Customer journey mapping',
      'Predictive customer behaviour modelling',
      'Churn risk identification',
      'Personalisation recommendations',
      'GDPR compliance monitoring',
      'UK customer service standards'
    ],
    pricing: { setup: 30720, monthly: 1413 },
    color: 'from-blue-500 to-indigo-500'
  },
  {
    id: 'customer-health-scoring',
    title: 'Customer Health Scoring System',
    subtitle: 'Predictive Customer Success',
    description: 'Monitors customer usage patterns and behaviors to predict churn risk and identify expansion opportunities with AI-driven insights.',
    icon: Heart,
    image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?auto=format&fit=crop&w=800&q=80',
    category: 'Customer Success & CRM',
    status: 'Available',
    businessTypes: ['#customer-success', '#churn-prediction', '#customer-health', '#retention', '#growth'],
    features: [
      'Real-time health score calculation',
      'Behavioural pattern analysis',
      'Risk alert automation',
      'Expansion opportunity identification',
      'Custom scoring model configuration',
      'Integration with CRM and support systems',
      'GDPR data handling compliance',
      'UK business standards alignment'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 'onboarding-automation',
    title: 'Onboarding Automation Platform',
    subtitle: 'Customer Success Automation',
    description: 'Guides new customers through implementation with personalized checklists, progress tracking, and automated milestone celebrations.',
    icon: UserCheck,
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80',
    category: 'Customer Success & CRM',
    status: 'Available',
    businessTypes: ['#onboarding', '#customer-success', '#automation', '#user-experience', '#retention'],
    features: [
      'Personalized onboarding workflows',
      'Progress tracking and milestones',
      'Automated reminder and follow-up systems',
      'Resource and tutorial integration',
      'Success metrics and analytics',
      'Team collaboration and handoffs'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'customer-feedback-management',
    title: 'Customer Feedback Management',
    subtitle: 'Voice of Customer Platform',
    description: 'Collects, analyses, and acts upon customer feedback from multiple channels to drive product improvements and satisfaction with UK consumer rights compliance.',
    icon: MessageCircle,
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=800&q=80',
    category: 'Customer Success & CRM',
    status: 'Available',
    businessTypes: ['#feedback-management', '#voice-of-customer', '#product-improvement', '#satisfaction', '#surveys'],
    features: [
      'Multi-channel feedback collection',
      'Sentiment analysis and categorization',
      'Automated response and routing',
      'Trend identification and reporting',
      'Product team integration',
      'Closed-loop feedback processes'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-orange-500 to-red-500'
  },
  {
    id: 'digital-customer-service',
    title: 'Digital Customer Service Platform',
    subtitle: 'Omnichannel Support Hub',
    description: 'Comprehensive omnichannel support platform with AI-powered routing, response suggestions, and integrated knowledge management.',
    icon: Headphones,
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=800&q=80',
    category: 'Customer Success & CRM',
    status: 'Available',
    businessTypes: ['#customer-service', '#omnichannel', '#support', '#ai-powered', '#help-desk'],
    features: [
      'Unified inbox for all channels',
      'AI-powered ticket routing and prioritization',
      'Smart response suggestions',
      'Integrated knowledge base',
      'Performance analytics and reporting',
      'Customer satisfaction tracking'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-cyan-500 to-blue-500'
  },
  {
    id: 'community-engagement',
    title: 'Community Engagement Platform',
    subtitle: 'Customer Community Hub',
    description: 'Creates branded customer communities for peer-to-peer support, idea sharing, product feedback, and customer engagement.',
    icon: Globe,
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80',
    category: 'Customer Success & CRM',
    status: 'Available',
    businessTypes: ['#community', '#customer-engagement', '#peer-support', '#brand-building', '#user-generated-content'],
    features: [
      'Branded community portal',
      'Discussion forums and groups',
      'Gamification and rewards',
      'Expert and moderator tools',
      'Content management and moderation',
      'Community analytics and insights'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-teal-500 to-green-500'
  },
  {
    id: 'customer-success-playbooks',
    title: 'Customer Success Playbook Automation',
    subtitle: 'Success Process Automation',
    description: 'Standardizes and automates customer success workflows based on best practices, segmentation, and proven methodologies.',
    icon: PlayCircle,
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&q=80',
    category: 'Customer Success & CRM',
    status: 'Available',
    businessTypes: ['#customer-success', '#playbooks', '#automation', '#best-practices', '#workflow'],
    features: [
      'Pre-built success playbooks',
      'Automated workflow execution',
      'Customer segmentation and targeting',
      'Performance tracking and optimization',
      'Custom playbook creation',
      'Team collaboration and handoffs'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-violet-500 to-purple-500'
  },
  {
    id: 'revenue-intelligence',
    title: 'Revenue Intelligence Platform',
    subtitle: 'Sales Performance Analytics',
    description: 'Captures and analyses sales interactions to provide actionable insights for improving conversion rates and deal sizes in UK market.',
    icon: TrendingUp,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
    category: 'Customer Success & CRM',
    status: 'Available',
    businessTypes: ['#revenue-intelligence', '#sales-analytics', '#conversion-optimization', '#deal-insights', '#performance'],
    features: [
      'Call and meeting analysis',
      'Deal risk assessment',
      'Win/loss analysis and insights',
      'Coaching recommendations',
      'Forecast accuracy improvement',
      'Competitive intelligence tracking'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-yellow-500 to-orange-500'
  },
  {
    id: 'contract-lifecycle-management',
    title: 'Contract Lifecycle Management',
    subtitle: 'Legal Operations Platform',
    description: 'Manages the complete contract process from creation through negotiation, execution, and renewal with compliance tracking.',
    icon: FileText,
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80',
    category: 'Customer Success & CRM',
    status: 'Available',
    businessTypes: ['#contract-management', '#legal-operations', '#compliance', '#negotiations', '#renewals'],
    features: [
      'Contract authoring and templates',
      'Negotiation and approval workflows',
      'Electronic signature integration',
      'Renewal and expiration tracking',
      'Compliance monitoring and reporting',
      'Contract analytics and insights'
    ],
    pricing: { setup: 30720, monthly: 1413 },
    color: 'from-red-500 to-pink-500'
  },
  {
    id: 'customer-journey-analytics',
    title: 'Customer Journey Analytics',
    subtitle: 'Experience Optimisation Platform',
    description: 'Visualises and optimises the end-to-end customer experience across all touchpoints and channels with actionable insights for UK market.',
    icon: MapPin,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
    category: 'Customer Success & CRM',
    status: 'Available',
    businessTypes: ['#customer-journey', '#experience-optimization', '#touchpoint-analysis', '#conversion', '#analytics'],
    features: [
      'Journey mapping and visualization',
      'Touchpoint performance analysis',
      'Conversion funnel optimization',
      'Attribution modeling',
      'Real-time journey tracking',
      'Experience personalization recommendations'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-indigo-500 to-blue-500'
  }
];